import {_} from 'lodash';
import * as ko from "knockout";
import {ICustomMessageType, IType, TType} from "./index";
import {IMessage, IMessageType, IRuleset, TBaseOrObservableType} from "../../index";
import {global, lm} from "../../appContext";
import {nsMessageFlowClone} from "../../clone/nsMessageFlowClone";
import {stringSplitCondition} from "../../util/misc";
import {nsDynamicVariable} from "../dynamic-variable/nsDynamicVariable";

export namespace nsMessages {

    export enum MessageTypeId {
        SmsUser = 1,
        SmsManager = 2,
        EmailManager = 3,
        EmailUser = 4,
        EmailCustom = 5,
        eAdmUserTask = 6,
        eAdmManagerTask = 7,
        eAdmUserMessage = 8,
        eAdmManagerMessage = 9,
        SmsCustom = 10,
    }

    export const type2id: Record<string, IMessageType['id']> = {
        'sms user': MessageTypeId.SmsUser,
        'sms manager': MessageTypeId.SmsManager,
        'email manager': MessageTypeId.EmailManager,
        'email user': MessageTypeId.EmailUser,
        'email custom': MessageTypeId.EmailCustom,
        'eadm user task': MessageTypeId.eAdmUserTask,
        'eadm manager task': MessageTypeId.eAdmManagerTask,
        'eadm user message': MessageTypeId.eAdmUserMessage,
        'eadm manager message': MessageTypeId.eAdmManagerMessage,
        'sms custom': MessageTypeId.SmsCustom,
    }

    export const id2type: Record<IMessageType['id'], string> = {
        [MessageTypeId.SmsUser]: 'sms user',
        [MessageTypeId.SmsManager]: 'sms manager',
        [MessageTypeId.EmailManager]: 'email manager',
        [MessageTypeId.EmailUser]: 'email user',
        [MessageTypeId.EmailCustom]: 'email custom',
        [MessageTypeId.eAdmUserTask]: 'eadm user task',
        [MessageTypeId.eAdmManagerTask]: 'eadm manager task',
        [MessageTypeId.eAdmUserMessage]: 'eadm user message',
        [MessageTypeId.eAdmManagerMessage]: 'eadm manager message',
        [MessageTypeId.SmsCustom]: 'sms custom',
    }

    export const CustomMessageTypes: Record<TType, IType> = {
        'sms': {
            icon: 'fa-comment',
            key: 'sms',
            label: 'sms',
            recipientType: 'phone number',
        },
        'email': {
            icon: 'fa-envelope',
            key: 'email',
            label: 'email',
            recipientType: 'email',
        },
        'eadm': {
            icon: 'fa-puzzle-piece',
            key: 'eadm',
            label: 'eadm',
        },
        'custom': {
            icon: 'fa-gear',
            key: 'custom',
            label: 'custom',
        },
        'manager': {
            icon: 'fa-user-tie',
            key: 'manager',
            label: 'manager',
        },
        'message': {
            icon: 'fa-message',
            key: 'message',
            label: 'message',
        },
        'task': {
            icon: 'fa-clipboard',
            key: 'task',
            label: 'form',
        },
        'user': {
            icon: 'fa-user',
            key: 'user',
            label: 'user',
        }
    };


    export function getCustomMessageTypes(): Promise<ICustomMessageType[]> {
        return global.getPromiseRequest('getmessagetypes')
            .request()
            .then((data: IMessageType[]) => {
                return data.map((mt: IMessageType): ICustomMessageType => {
                    return {
                        $original: mt,
                        maintype: getMainType(mt.name),
                        subtype: getSubType(mt.name),
                    };
                })
            });
    }

    export function getMainType(name: string): IType | null {
        if (/sms/i.test(name)) return CustomMessageTypes['sms'];
        if (/e-post/i.test(name)) return CustomMessageTypes['email'];
        if (/eAdm|skjemaoppgave/i.test(name)) return CustomMessageTypes['eadm'];
        return null;
    }

    export function getSubType(name: string): IType | null {
        if (/bruker|^skjemaoppgave$/i.test(name)) return CustomMessageTypes['user']
        if (/manager|leder$/i.test(name)) return CustomMessageTypes['manager']
        if (/^e-post$/i.test(name)) return CustomMessageTypes['custom']
        if (/^sms egendefinert$/i.test(name)) return CustomMessageTypes['custom']
        return null;
    }

    export function getMinorType(name: string): IType | null {
        if (/Skjemaoppgave/i.test(name)) return CustomMessageTypes['task'];
        if (/eadm/i.test(name)) return CustomMessageTypes['message'];
        return null;
    }

    export function getCustomIcon(name: string): string {
        const main = getMainType(name);
        const sub = getSubType(name);
        return main && sub ? `fa-${main.key}-${sub.key}` : '';
    }

    export function getCustomMessageObject(name: string): {
        main: IType | null,
        sub: IType | null,
        minor: IType | null,
        label: string,
        icon: string
    } {
        const main = getMainType(name);
        const sub = getSubType(name);
        const minor = getMinorType(name);
        let l: string[] = [];
        if (main) {
            l.push(lm.t(`((medium)): ((${main.label}))`) ?? '')
        }
        if (sub) {
            l.push(lm.t(`((recipient)): ((${sub.label}))`) ?? '')
        }
        if (minor) {
            l.push(lm.t(`((type)): ((${minor.label}))`) ?? '')
        }
        const label = l.join('\n');
        const icon = main && sub ? `fa-${main.key}-${sub.key}` : '';
        return {main, sub, minor, label, icon};
    }

    export function getAddRecipientLabel(id: IMessageType['id']): string {
        const [main, sub] = getType(id);
        return (sub == 'custom')
            ? 'recipient|p'
            : 'copy to'
    }

    function getType(id: IMessageType['id']): TType[] {
        switch (id) {
            case 1:
                return ['sms', 'user'];
            case 2:
                return ['sms', 'manager'];
            case 3:
                return ['email', 'manager'];
            case 4:
                return ['email', 'user'];
            case 5:
                return ['email', 'custom'];
            case 6:
                return ['task', 'user'];
            case 7:
                return ['task', 'manager'];
            case 8:
                return ['message', 'user'];
            case 9:
                return ['message', 'manager'];
            case 10:
                return ['sms', 'custom'];
        }
    }

    export class TypeChecker {

        constructor(private messagetype: TBaseOrObservableType<IMessageType | null>) {
        }

        public getType(): TType[] {
            const id = ko.unwrap(this.messagetype)?.id;
            return id != null ? getType(id) : [];
        }

        public isType(...types: TType[]): boolean {
            return !!types.find(t => this._isType(t));
        }

        private _isType(type: TType): boolean {
            switch (type) {
                case "sms":
                    return this._isTypeId(1, 2, 10);
                case "email":
                    return this._isTypeId(3, 4, 5);
                case "eadm":
                    return this._isTypeId(6, 7, 8, 9);
                case "user":
                    return this._isTypeId(1, 4, 6, 8);
                case "manager":
                    return this._isTypeId(2, 3, 7, 9);
                case "custom":
                    return this._isTypeId(5, 10);
                case "task":
                    return this._isTypeId(6, 7);
                case "message":
                    return this._isTypeId(8, 9);
            }

        }

        private _isTypeId(...ids: IMessageType['id'][]): boolean {
            const id = ko.unwrap(this.messagetype)?.id;
            return id != null && ids.includes(id);
        }

    }

    export function copyMessageDialogWithoutLog(data: IMessage) {
        const d = _.clone(data)
        delete d.messagelog
        nsMessageFlowClone.copyDialog(d)
    }


    export function splitRecipients(strRecipients: string): string[] {
        return stringSplitCondition(strRecipients, (char: string, index: number) => {
            return char == ',' && !nsDynamicVariable.getTextRange(index, strRecipients)
        });
    }

    export function getRuleWithFormId(ruleset: IRuleset) {
        return ruleset.rules.find(r => {
            const a = r.attribute == 'FormId';
            const b = parseInt(r.argument.toString()) == r.argument;
            return a && b
        })
    }

    export const MessageTypeIdToName: Record<IMessage['messagetypeid'], IMessage['messagetypename']> = {
        [MessageTypeId.SmsUser]: 'SMS bruker',
        [MessageTypeId.SmsManager]: 'SMS Manager',
        [MessageTypeId.EmailManager]: 'E-post Manager',
        [MessageTypeId.EmailUser]: 'E-post Bruker',
        [MessageTypeId.EmailCustom]: 'E-post',
        [MessageTypeId.eAdmUserTask]: 'Skjemaoppgave',
        [MessageTypeId.eAdmManagerTask]: 'Skjemaoppgave leder',
        [MessageTypeId.eAdmUserMessage]: 'eAdm bruker',
        [MessageTypeId.eAdmManagerMessage]: 'eAdm leder',
        [MessageTypeId.SmsCustom]: 'SMS egendefinert',
    }

}
